<template>
  <aside :class="['chat-panel', { open: uiStore.chatPanelOpen }]">
    <div class="chat-resizer" @mousedown="startResize"></div>

    <div class="finagent-header">
      <div class="finagent-brand">
        <div class="finagent-logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <polygon points="12,2 14,9 21,9 15.5,13.5 17.5,20.5 12,16 6.5,20.5 8.5,13.5 3,9 10,9" fill="currentColor" opacity="0.9"/>
          </svg>
        </div>
        <span class="finagent-name">FinAgent</span>
        <span :class="['finagent-status', isConnected ? 'connected' : '']">{{ isConnected ? 'Streaming' : 'Ready' }}</span>
      </div>
      <div class="header-actions">
        <button class="thread-new-btn" @click="handleNewThread" title="New Thread">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </button>
        <div class="thread-dropdown-wrapper">
          <button class="thread-menu-btn" @click.stop="showThreadMenu = !showThreadMenu" title="Thread options">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
          </button>
          <div v-if="showThreadMenu" class="thread-dropdown" @click.stop>
            <div class="thread-dropdown-section">
              <span class="thread-dropdown-label">Threads</span>
              <button
                v-for="thread in chatStore.threads"
                :key="thread.id"
                :class="['thread-dropdown-item', { active: thread.id === chatStore.activeThreadId }]"
                @click="switchToThread(thread.id)"
              >
                <span class="thread-dropdown-item-name">{{ thread.name }}</span>
                <span class="thread-dropdown-item-count">{{ thread.messages.length }}</span>
                <button
                  v-if="thread.id !== 'default'"
                  class="thread-delete-btn"
                  @click.stop="handleDeleteThread(thread.id)"
                  title="Delete thread"
                >&times;</button>
              </button>
            </div>
            <div class="thread-dropdown-actions">
              <button class="thread-dropdown-action" @click="handleClearChat">Clear Chat</button>
            </div>
          </div>
        </div>
        <button @click="uiStore.toggleChatPanel" class="finagent-close-btn" aria-label="Close chat">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>

    <div class="chat-thread-bar" @click.stop="showThreadList = !showThreadList">
      <span class="chat-thread-title">{{ chatStore.activeThread?.name || 'Default' }}</span>
      <span class="chat-thread-count">{{ chatStore.threads.length }}</span>
      <svg :class="['thread-chevron', { open: showThreadList }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </div>

    <div v-if="showThreadList" class="thread-list" @click.stop>
      <button
        v-for="thread in chatStore.threads"
        :key="thread.id"
        :class="['thread-list-item', { active: thread.id === chatStore.activeThreadId }]"
        @click.stop="switchToThread(thread.id)"
      >
        <span class="thread-list-item-name">{{ thread.name }}</span>
        <span class="thread-list-item-count">{{ thread.messages.length }}</span>
        <button
          v-if="thread.id !== 'default'"
          class="thread-delete-btn"
          @click.stop="handleDeleteThread(thread.id)"
          title="Delete thread"
        >&times;</button>
      </button>
      <button class="thread-list-new" @click.stop="handleNewThread">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        New Thread
      </button>
    </div>

    <div class="chat-messages" ref="messagesContainer">
      <div
        v-for="(message, index) in chatStore.messages"
        :key="index"
        :class="['msg', message.role]"
      >
        <div class="avatar" :class="message.role === 'user' ? 'user' : 'ai'">
          {{ message.role === 'user' ? 'U' : 'AI' }}
        </div>
        <div class="msg-content">
          <div v-if="message.role === 'assistant'" v-html="renderedContent(message.content)" class="markdown"></div>
          <div v-else>{{ message.content }}</div>
          <div v-if="message.chart || (message.charts && message.charts.length)" class="msg-charts">
            <ChartWrapper
              v-if="message.chart"
              :chart-id="`chart-${message.id}`"
              :type="message.chart.type || 'line'"
              :data="chartToChartData(message.chart)"
              :title="message.chart.title || ''"
            />
            <ChartWrapper
              v-for="(ch, ci) in (message.charts || [])"
              :key="ci"
              :chart-id="`chart-${message.id}-${ci}`"
              :type="ch.type || 'line'"
              :data="chartToChartData(ch)"
              :title="ch.title || ''"
            />
          </div>
          <div v-if="message.progressLines && message.progressLines.length" class="msg-progress">
            <div class="msg-progress-header" @click="toggleMsgProgress(message.id)">
              <span>Agent Progress ({{ message.progressLines.length }})</span>
              <svg :class="['progress-chevron', { open: expandedMsgProgress[message.id] }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            <div v-if="expandedMsgProgress[message.id]" class="msg-progress-lines">
              <div v-for="(line, li) in message.progressLines" :key="li" class="msg-progress-line">
                <span class="msg-progress-time">{{ line.time }}</span>
                <span class="msg-progress-text">{{ line.text }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="chatStore.streamingMessage" class="msg assistant streaming">
        <div class="avatar ai">AI</div>
        <div class="msg-content">
          <div v-html="renderedContent(chatStore.streamingMessage)" class="markdown"></div>
        </div>
      </div>

      <div v-if="chatStore.isLoading && !chatStore.streamingMessage" class="loading-indicator">
        <div class="spinner-ring"></div>
        <span class="spinner-label">Working on it…</span>
      </div>

      <div v-if="chatStore.progressLines.length > 0" class="streaming-progress">
        <div class="streaming-progress-header" @click="showStreamingProgress = !showStreamingProgress">
          <span>Agent Progress ({{ chatStore.progressLines.length }})</span>
          <svg :class="['progress-chevron', { open: showStreamingProgress }]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
        <div v-if="showStreamingProgress" class="streaming-progress-log">
          <div v-for="(line, index) in chatStore.progressLines" :key="index" class="streaming-progress-line">
            <span class="streaming-progress-line-time">{{ line.time }}</span>
            <span class="streaming-progress-line-text">{{ line.text }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="chat-input-area">
      <div class="quick-btns">
        <button type="button" @click="insertQuickQuery('Gold forecast')">Gold</button>
        <button type="button" @click="insertQuickQuery('Silver forecast')">Silver</button>
        <button type="button" @click="insertQuickQuery('Copper forecast')">Copper</button>
        <button type="button" @click="insertQuickQuery('Bitcoin forecast')">Bitcoin</button>
      </div>
      <form @submit.prevent="submitMessage" class="input-row">
        <textarea
          v-model="inputMessage"
          placeholder="Ask about markets, strategies, timing..."
          class="chat-textarea"
          rows="1"
          @input="autoResize"
          @keydown.enter.exact.prevent="submitMessage"
          ref="textareaRef"
        ></textarea>
        <button v-if="chatStore.isLoading" type="button" class="stop-mode" @click="chatStore.stopStreaming()" title="Stop generating">
          <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
            <rect x="6" y="6" width="12" height="12" rx="2"></rect>
          </svg>
        </button>
        <button v-else type="submit" class="send-btn" :disabled="!inputMessage.trim()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </form>
    </div>
  </aside>
</template>

<script setup>
import { ref, nextTick, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { useUIStore } from '../stores/ui';
import { useChatStore } from '../stores/chat';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import ChartWrapper from './ChartWrapper.vue';

const uiStore = useUIStore();
const chatStore = useChatStore();
const inputMessage = ref('');
const messagesContainer = ref(null);
const textareaRef = ref(null);
const showThreadMenu = ref(false);
const showThreadList = ref(false);
const showStreamingProgress = ref(true);
const expandedMsgProgress = ref({});

const toggleMsgProgress = (msgId) => {
  expandedMsgProgress.value = {
    ...expandedMsgProgress.value,
    [msgId]: !expandedMsgProgress.value[msgId]
  };
};

const isConnected = computed(() => chatStore.isConnected);

const renderedContent = (content) => {
  const raw = marked.parse(content || '');
  return DOMPurify.sanitize(raw);
};

const chartToChartData = (chart) => {
  if (!chart) return { labels: [], datasets: [] };
  return {
    labels: chart.labels || [],
    datasets: (chart.datasets || []).map(ds => ({
      ...ds,
      data: ds.data || [],
    })),
  };
};

const isUserNearBottom = () => {
  const el = messagesContainer.value;
  if (!el) return true;
  const threshold = 80; // px from bottom
  return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
};

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const autoResize = () => {
  const textarea = textareaRef.value;
  if (!textarea) return;
  textarea.style.height = 'auto';
  textarea.style.height = `${Math.min(textarea.scrollHeight, 240)}px`;
};

const insertQuickQuery = (query) => {
  inputMessage.value = query;
  autoResize();
  textareaRef.value?.focus();
};

// Only auto-scroll while streaming if user is near bottom — don't force scroll when user reads earlier content
watch(() => chatStore.streamingMessage, (val) => {
  if (val && chatStore.isLoading && isUserNearBottom()) scrollToBottom();
});

onMounted(() => {
  autoResize();
  document.addEventListener('click', closeThreadMenu);
  document.addEventListener('click', closeThreadList);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeThreadMenu);
  document.removeEventListener('click', closeThreadList);
});

const closeThreadMenu = () => {
  showThreadMenu.value = false;
};

const closeThreadList = () => {
  showThreadList.value = false;
};

const handleNewThread = () => {
  showThreadMenu.value = false;
  showThreadList.value = false;
  const name = `Thread ${chatStore.threads.length}`;
  chatStore.createThread(name);
  nextTick(scrollToBottom);
};

const switchToThread = (threadId) => {
  chatStore.switchThread(threadId);
  showThreadMenu.value = false;
  showThreadList.value = false;
  nextTick(scrollToBottom);
};

const handleDeleteThread = (threadId) => {
  chatStore.deleteThread(threadId);
  nextTick(scrollToBottom);
};

const handleClearChat = () => {
  chatStore.clearMessages();
  showThreadMenu.value = false;
};

const submitMessage = async () => {
  if (!inputMessage.value.trim()) return;
  if (chatStore.isLoading) {
    chatStore.stopStreaming();
  }
  const msg = inputMessage.value;
  inputMessage.value = '';
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto';
  }
  await chatStore.sendMessage(msg);
};

const startResize = (e) => {
  const startX = e.clientX;
  const startWidth = 380;

  const handleMouseMove = (moveEvent) => {
    const deltaX = startX - moveEvent.clientX;
    const newWidth = Math.max(280, Math.min(600, startWidth + deltaX));
    document.documentElement.style.setProperty('--chat-w', `${newWidth}px`);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};
</script>

<style scoped>
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.thread-new-btn {
  background: rgba(243, 210, 122, 0.18);
  border: 1px solid rgba(243, 210, 122, 0.3);
  color: #f3d27a;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.thread-menu-btn {
  background: none;
  border: none;
  color: var(--text-secondary, #64748b);
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.thread-new-btn svg,
.thread-menu-btn svg {
  width: 16px;
  height: 16px;
}

.thread-new-btn:hover {
  background: rgba(243, 210, 122, 0.32);
  color: #f3d27a;
}

.thread-menu-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--text-primary, #1e293b);
}

.thread-dropdown-wrapper {
  position: relative;
}

.thread-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  z-index: 200;
  overflow: hidden;
}

.thread-dropdown-section {
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.thread-dropdown-label {
  display: block;
  padding: 0.4rem 0.85rem 0.3rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-dim, #94a3b8);
}

.thread-dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.85rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #1e293b);
  transition: background 0.12s;
  gap: 0.5rem;
}

.thread-dropdown-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.thread-dropdown-item.active {
  background: var(--accent-dim, rgba(240, 185, 11, 0.08));
  color: var(--accent, #d97706);
  font-weight: 600;
}

.thread-dropdown-item-name {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thread-dropdown-item-count {
  font-size: 0.72rem;
  color: var(--text-dim, #94a3b8);
  background: rgba(0, 0, 0, 0.05);
  padding: 0.1rem 0.45rem;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
}

.thread-delete-btn {
  background: none;
  border: none;
  color: var(--text-dim, #94a3b8);
  cursor: pointer;
  font-size: 1rem;
  padding: 0 0.2rem;
  line-height: 1;
  border-radius: 4px;
  transition: color 0.12s, background 0.12s;
}

.thread-delete-btn:hover {
  color: var(--danger, #ef4444);
  background: rgba(239, 68, 68, 0.08);
}

.thread-dropdown-actions {
  padding: 0.3rem;
}

.thread-dropdown-action {
  display: block;
  width: 100%;
  padding: 0.45rem 0.85rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--text-secondary, #64748b);
  text-align: left;
  border-radius: 6px;
  transition: background 0.12s;
}

.thread-dropdown-action:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--text-primary, #1e293b);
}

.chat-thread-bar {
  padding: 0.4rem 0.9rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.015);
  cursor: pointer;
  transition: background 0.12s;
}

.chat-thread-bar:hover {
  background: rgba(0, 0, 0, 0.03);
}

.chat-thread-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary, #64748b);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.chat-thread-count {
  font-size: 0.68rem;
  color: var(--text-dim, #94a3b8);
  background: rgba(0, 0, 0, 0.06);
  padding: 0.1rem 0.4rem;
  border-radius: 6px;
  font-variant-numeric: tabular-nums;
}

.thread-chevron {
  width: 14px;
  height: 14px;
  color: var(--text-dim, #94a3b8);
  transition: transform 0.15s;
}

.thread-chevron.open {
  transform: rotate(180deg);
}

.thread-list {
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  background: rgba(0, 0, 0, 0.02);
  max-height: 200px;
  overflow-y: auto;
}

.thread-list-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.45rem 0.9rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--text-primary, #1e293b);
  transition: background 0.12s;
  gap: 0.5rem;
  text-align: left;
}

.thread-list-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.thread-list-item.active {
  background: var(--accent-dim, rgba(240, 185, 11, 0.08));
  color: var(--accent, #d97706);
  font-weight: 600;
}

.thread-list-item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.thread-list-item-count {
  font-size: 0.7rem;
  color: var(--text-dim, #94a3b8);
  background: rgba(0, 0, 0, 0.05);
  padding: 0.1rem 0.4rem;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
}

.thread-list-new {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
  padding: 0.45rem 0.9rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--text-dim, #94a3b8);
  transition: background 0.12s, color 0.12s;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

.thread-list-new:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--text-primary, #1e293b);
}

.msg-charts {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.msg-charts :deep(.chart-wrapper) {
  background: rgba(252, 250, 246, 0.98);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 12px;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(29, 36, 48, 0.06);
  backdrop-filter: blur(8px);
}

.msg-charts :deep(.chart-wrapper canvas) {
  max-height: 260px;
}

.msg-progress {
  margin-top: 0.6rem;
  background: #1a1610;
  border: 1px solid rgba(182, 138, 46, 0.15);
  border-radius: 8px;
  padding: 0.35rem 0.55rem;
  font-size: 0.72rem;
  min-height: auto;
  max-height: 4.5rem;
  overflow-y: auto;
  box-shadow: inset 0 1px 6px rgba(0,0,0,0.25);
  font-family: 'JetBrains Mono', 'Fira Code', 'IBM Plex Mono', monospace;
}

.msg-progress-header {
  font-weight: 800;
  margin-bottom: 0.35rem;
  color: rgba(182, 138, 46, 0.7);
  font-size: 0.58rem;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding: 0.15rem 0;
}

.msg-progress-header:hover {
  color: rgba(182, 138, 46, 1);
}

.msg-progress-lines {
  border-top: 1px solid rgba(182, 138, 46, 0.15);
  padding-top: 0.3rem;
}

.progress-chevron {
  width: 14px;
  height: 14px;
  color: var(--text-dim, #94a3b8);
  transition: transform 0.15s;
  flex-shrink: 0;
}

.progress-chevron.open {
  transform: rotate(180deg);
}

.msg-progress-line {
  display: flex;
  gap: 0.55rem;
  padding: 0.15rem 0;
  align-items: flex-start;
}

.msg-progress-line::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--gold);
  box-shadow: 0 0 10px var(--gold);
  flex: 0 0 auto;
  margin-top: 0.35rem;
}

.msg-progress-time {
  color: rgba(182, 138, 46, 0.5);
  font-family: var(--font-mono);
  font-size: 0.62rem;
  font-weight: 700;
  flex-shrink: 0;
}

.msg-progress-text {
  color: #d4c4a8;
  word-break: break-word;
  flex: 1;
}

.markdown :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
  font-size: 0.82rem;
  display: block;
  overflow-x: auto;
}

.markdown :deep(thead) {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.04));
}

.markdown :deep(th) {
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color, #e2e8f0);
  white-space: nowrap;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--text-secondary, #64748b);
}

.markdown :deep(td) {
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  color: var(--text-primary, #1e293b);
  white-space: nowrap;
}

.markdown :deep(tbody tr:hover) {
  background: var(--bg-tertiary, rgba(0, 0, 0, 0.02));
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
}

.spinner-ring {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--accent, #d97706);
  border-radius: 50%;
  animation: spin-ring 0.8s linear infinite;
}

.spinner-label {
  font-size: 0.82rem;
  color: var(--text-secondary, #64748b);
  font-style: italic;
}

@keyframes spin-ring {
  to { transform: rotate(360deg); }
}
</style>
